import { Navigate, Outlet, useLocation } from "react-router-dom";
import { PageLoader } from "@/layouts/components/PageLoader/PageLoader";
import { useOfferRequest, useLoanOffer } from "@/features/offer";
import { useKyc } from "@/features/kyc";
import { routes } from "../routes";
import type { LoanOfferState, OfferRequest } from "@/features/offer/types";
import {
  getVoltAlternatives,
  hasAvailableOffer,
  hasCurrentFinalOffer,
} from "@/lib/offerUtils";
import { useApplication } from "@/features/application/hooks";
import type { ApplicationState } from "@/features/application";

export const OfferGuard = () => {
  const location = useLocation();
  const { offer, isLoading } = useOfferRequest(true);
  const {
    proofOfIncomeRequired,
    proofOfIncomeProcessing,
    loanOfferState,
    isLoading: isLoanOfferLoading,
  } = useLoanOffer(true);
  const { applicationState, isApplicationStateLoading } = useApplication(true);
  const { isKycLoading, isKycValid } = useKyc(true);

  if (
    isLoading ||
    isLoanOfferLoading ||
    isApplicationStateLoading ||
    isKycLoading
  ) {
    return <PageLoader />;
  }

  const nextStep = getNextOfferStep(
    offer,
    loanOfferState,
    proofOfIncomeProcessing,
    applicationState,
    !!isKycValid,
  );

  // proof-of-income upload is reachable on demand while an offer is missing,
  // and the Volt Zero details while it is the product offered instead
  const isFreeRoute =
    (location.pathname === routes.offer.proofOfIncome && proofOfIncomeRequired) ||
    (location.pathname === routes.offer.voltZeroLanding &&
      nextStep === routes.offer.voltAlternative);

  if (location.pathname !== nextStep && !isFreeRoute) {
    return <Navigate to={nextStep} replace />;
  }

  return <Outlet />;
};

// Nothing to apply for: a Volt product offered instead has its own screen,
// unless the income is to be confirmed first.
const getNoOfferStep = (loanOfferState: LoanOfferState | undefined) =>
  !loanOfferState?.proofOfIncomeRequired &&
  getVoltAlternatives(loanOfferState?.alternativeCreditOffers).length > 0
    ? routes.offer.voltAlternative
    : routes.offer.notAvailable;

const getNextOfferStep = (
  offer: OfferRequest | undefined,
  loanOfferState: LoanOfferState | undefined, // Replace 'any' with the correct type
  proofOfIncomeProcessing: boolean,
  application: ApplicationState | undefined,
  isKycValid: boolean,
) => {
  // The lender's final offer answers before Volt's request: without a request
  // (applied at Ocean directly) one would be made here, asking for consents
  // the offer no longer needs.
  if (hasCurrentFinalOffer(application)) {
    if (proofOfIncomeProcessing) {
      return routes.offer.proofOfIncome;
    }
    return hasAvailableOffer(loanOfferState?.creditOffers)
      ? routes.application.base
      : getNoOfferStep(loanOfferState);
  }

  // With KYC done the request is stale: the backend never resumes an expired
  // one. The ANAF waiting screen requests the offer again, which replaces it.
  if (offer?.status === "KycRequired" && !isKycValid) {
    return routes.kyc.base;
  }

  // ANAF alone: the BC consent was given before the BC score.
  if (offer?.status === "RequiredAnafTerms") {
    return routes.terms.anafConsent;
  }

  if (offer?.status === "RequiredBcTerms") {
    return routes.offer.terms;
  }

  if (proofOfIncomeProcessing) {
    return routes.offer.proofOfIncome;
  }

  // An offer the user can actually act on wins over the request status: the
  // offer request can conclude NoOfferAvailable while /loans/offers still
  // carries an available offer. `not-available` is only correct when every
  // credit offer is unavailable.
  if (
    offer?.status === "NoOfferAvailable" ||
    offer?.status === "Complete"
  ) {
    return hasAvailableOffer(loanOfferState?.creditOffers)
      ? routes.application.base
      : getNoOfferStep(loanOfferState);
  }

  if (offer?.status === "Failed") {
    return routes.offer.inProgress;
  }

  return routes.offer.anafInProgress;
};
